import React, { useState } from "react";
import classes from "./LrHistory.module.css";

const ShowLrHistory = ({ changeLog }) => {
    const [expandedIndexes, setExpandedIndexes] = useState({});

    const fieldMapping = {
        display_lr_id: "LR No.",
        vehicle_no: "Truck No.",
        eway_bill_no: "Eway Bill No.",
        user_lr_date: "LR Date",
        lr_type: "LR Type",
        sales_invoice_no_list: "Sales Invoice No.",
        consignee_details: "Consignee Details",
        consignor_details: "Consignor Details",
        transporter_details: "Transporter Details",
        consignment_details: "Consignment Details",
        last_edited_by: "Role",
        filename: "Attachment",
        lr_generated: "Status",
        additional_details: "Additional Charges"
    };

    const isEmptyValue = (val) => {
        if (val === null || val === undefined || val === "") return true;
        if (Array.isArray(val) && val.length === 0) return true;
        if (typeof val === "object" && Object.keys(val).length === 0) return true;
        return false;
    };

    const formatIST = (timestamp) => {
        if (!timestamp) return "NA";
        try {
            const dateStr = timestamp.replace(" ", "T");
            const date = new Date(dateStr.endsWith("Z") ? dateStr : dateStr + "Z");
            return new Intl.DateTimeFormat('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
                timeZone: 'Asia/Kolkata'
            }).format(date);
        } catch (e) {
            return timestamp;
        }
    };

    const toggleExpand = (index) => {
        setExpandedIndexes(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const ValueRenderer = ({ data, field }) => {
        if (isEmptyValue(data)) return <span className={`text-muted ${classes["italic"]}`}>Not Set</span>;

        const simplify = (val) => {
            if (typeof val === "object" && val !== null) {
                return val.location_name || val.label || val.value || JSON.stringify(val);
            }
            if (typeof val === "string" && (val.includes("/") || val.includes("\\"))) {
                return val.split(/[\\/]/).pop();
            }
            if (val === true) return "Yes";
            if (val === false) return "No";
            return val?.toString() || "NA";
        };

        if (field === "consignment_details" && Array.isArray(data)) {
            return (
                <div className="consignment-details-container w-100 overflow-auto">
                    <table className="table table-sm table-bordered mb-0" style={{ fontSize: '11px', minWidth: '600px' }}>
                        <thead className="thead-light">
                            <tr>
                                <th>Document No.</th>
                                <th>Material Description</th>
                                <th>Material Code</th>
                                <th>Quantity</th>
                                <th>Unit Weight (KG)</th>
                                <th>Sales Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.length > 0 ? (
                                data.flatMap((item, idx) =>
                                    (item.orders_list || []).map((order, oIdx) => (
                                        <tr key={`${idx}-${oIdx}`}>
                                            <td className="font-weight-bold">{item.invoice_no || "NA"}</td>
                                            <td>{order.material_name || "NA"}</td>
                                            <td>{order.material_code || "NA"}</td>
                                            <td>{order.quantity || "0"}</td>
                                            <td>{order.material_weight || "0"}</td>
                                            <td>{order.invoice_value || "0"}</td>
                                        </tr>
                                    ))
                                )
                            ) : (
                                <tr>
                                    <td colSpan="6" className={`text-center text-muted ${classes["italic"]}`}>No items</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            );
        }

        if (field === "additional_details" && Array.isArray(data)) {
            return (
                <div className="w-100 overflow-auto">
                    <table className="table table-sm table-bordered mb-0" style={{ fontSize: '11px' }}>
                        <thead className="thead-light">
                            <tr>
                                <th>Charge Name</th>
                                <th>Value</th>
                                <th>Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((item, idx) => (
                                <tr key={idx}>
                                    <td>{item.name || "NA"}</td>
                                    <td className="font-weight-bold">
                                        {item.type === "amount" ? "₹ " : ""}{item.value ?? 0}
                                        {item.type === "percent" || item.type === "gst" ? " %" : ""}
                                        {item.type === "days" ? " Days" : ""}
                                    </td>
                                    <td className="text-capitalize">{item.type || "NA"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        }

        if (typeof data === "object" && !Array.isArray(data)) {
            const isCustomerObj = data.customer_name !== undefined || data.transporter_name !== undefined;
            if (isCustomerObj) {
                return (
                    <div className={classes["object-details"]}>
                        <div className={classes["detail-row"]}>
                            <span className="text-muted">Name: </span>
                            <span className={classes["detail-value"]}>{data.customer_name || data.transporter_name || "NA"}</span>
                        </div>
                        <div className={classes["detail-row"]}>
                            <span className="text-muted">GST No: </span>
                            <span className={classes["detail-value"]}>{data.gst_no || "NA"}</span>
                        </div>
                        <div className={classes["detail-row"]}>
                            <span className="text-muted">Address: </span>
                            <span className="detail-value">
                                {data.location?.location_name || data.addressLine1 || "NA"}
                            </span>
                        </div>
                    </div>
                );
            }

            return (
                <div className={classes["object-details"]}>
                    {Object.entries(data).map(([key, val]) => {
                        if (isEmptyValue(val)) return null;
                        const label = key.replace(/_/g, " ");
                        return (
                            <div key={key} className={classes["detail-row"]}>
                                <span className="text-muted text-capitalize">{label}: </span>
                                <span className={classes["detail-value"]}>{simplify(val)}</span>
                            </div>
                        );
                    })}
                </div>
            );
        }

        if (Array.isArray(data)) {
            return (
                <div className={classes["badge-container"]}>
                    {data.map((item, i) => (
                        <span key={i} className="badge badge-light border mr-1">
                            {simplify(item)}
                        </span>
                    ))}
                </div>
            );
        }

        return <span className={classes["value-text"]}>{simplify(data).toString()}</span>;
    };

    const filteredLog = changeLog
        ?.filter((log) => {
            const hiddenFields = ["last_edited_by", "filename", "lr_generated", "invoice_list"];
            if (hiddenFields.includes(log.field)) return false;
            if (JSON.stringify(log.old_value) === JSON.stringify(log.new_value)) return false;
            return true;
        })
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    const groupedLogs = [];
    if (filteredLog && filteredLog.length > 0) {
        let currentGroup = {
            timestamp: filteredLog[0].timestamp,
            user: filteredLog[0].last_edited_by || "System",
            changes: [filteredLog[0]]
        };

        for (let i = 1; i < filteredLog.length; i++) {
            const log = filteredLog[i];
            const prevLog = filteredLog[i - 1];
            const timeDiff = Math.abs(new Date(log.timestamp) - new Date(prevLog.timestamp));
            const sameUser = (log.last_edited_by || "System") === (prevLog.last_edited_by || "System");

            if (sameUser && timeDiff < 2000) {
                currentGroup.changes.push(log);
            } else {
                groupedLogs.push(currentGroup);
                currentGroup = {
                    timestamp: log.timestamp,
                    user: log.last_edited_by || "System",
                    changes: [log]
                };
            }
        }
        groupedLogs.push(currentGroup);
    }

    return (
        <div className={classes["lr-history-modern"]}>
            {groupedLogs.length > 0 ? (
                groupedLogs.map((group, groupIdx) => {
                    const isGen = group.changes.some(c => (c.field === "filename" || c.field === "lr_generated") && isEmptyValue(c.old_value));
                    const allNewAddition = group.changes.every(c => isEmptyValue(c.old_value));

                    const visibleChanges = group.changes.filter(c => c.field !== "filename" && c.field !== "lr_generated");

                    let actionText = "LR Details Updated";
                    if (isGen) actionText = "LR Generated";
                    else if (allNewAddition) actionText = "LR Details Added";

                    return (
                        <div key={groupIdx} className={classes["history-item"]}>
                            <div className={classes["history-header"]}>
                                <div>
                                    <p className={classes["history-title"]}>
                                        {actionText} by <span className={classes["history-user"]}>{group.user}</span>
                                    </p>
                                    <span className={classes["history-time"]}>{formatIST(group.timestamp)}</span>
                                </div>
                                {visibleChanges.length > 0 && (
                                    <button
                                        className={classes["show-more-btn"]}
                                        onClick={() => toggleExpand(groupIdx)}
                                    >
                                        {expandedIndexes[groupIdx] ? "Show Less" : "Show More"}
                                        <i className={`fas fa-chevron-${expandedIndexes[groupIdx] ? 'up' : 'down'}`}></i>
                                    </button>
                                )}
                            </div>

                            {expandedIndexes[groupIdx] && visibleChanges.length > 0 && (
                                <div className={classes["details-container"]}>
                                    {visibleChanges.map((log, logIdx) => {
                                        const isNewAdd = isEmptyValue(log.old_value);
                                        return (
                                            <div key={logIdx} className={classes["change-row"]}>
                                                <div className={classes["change-label"]}>
                                                    {fieldMapping[log.field] || log.field.replace(/_/g, " ")}
                                                </div>
                                                <div className={`${classes["change-values"]} ${isNewAdd || ["consignment_details", "consignee_details", "consignor_details", "transporter_details"].includes(log.field) ? classes["single-col"] : ""}`}>
                                                    {!isNewAdd && (
                                                        <div className={`${classes["value-box"]} ${classes["old"]}`}>
                                                            <span className={classes["value-type-label"]}>PREVIOUS VALUE</span>
                                                            <ValueRenderer data={log.old_value} field={log.field} />
                                                        </div>
                                                    )}
                                                    <div className={`${classes["value-box"]} ${isNewAdd ? classes["added"] : classes["new"]}`}>
                                                        <span className={classes["value-type-label"]}>{isNewAdd ? "NEWLY ADDED" : "MODIFIED VALUE"}</span>
                                                        <ValueRenderer data={log.new_value} field={log.field} />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })
            ) : (
                <div className="text-center py-5 text-muted bg-white rounded border">
                    <i className="fas fa-history fa-2x mb-3 d-block opacity-50"></i>
                    No edit history available.
                </div>
            )}
        </div>
    );
};

export default ShowLrHistory;
